// ShapeUtils.ts — pure, MobX-/three-free helpers for deriving point arrays from a MapObject's
// authored basic shape (Buildings Set1 §3.4 + §9 refactor note). Extracted out of
// `UI/Maps/MapUI/ObjectComps/Meshes_CompUI.tsx` so the DB-layer building accessors can use it
// without pulling the UI module graph (which imports react-vcomponents).
//
// Purity: this module is deliberately free of mobx-graphlink, three, AND the DB layer — it does
// NOT import `MapObject`/`ShapeType` (whose `@MapObject.js` home imports `mobx-graphlink`, which
// breaks the Node vitest lane). The shape type is passed as the plain string `"ellipse" |
// "rectangle"` (the exact string values of `ShapeType`), so this file imports cleanly anywhere.
import type {Vec2Simple} from "./VectorStructs.js";

export type ShapeTypeStr = "ellipse" | "rectangle";

/** The ellipse/rectangle footprint of a basic-shape object, as `Vec2Simple` points in the object's
 *  LOCAL frame. Mirrors the old `GetPointsMatchingObjShape(obj.shape_size, obj.shape_type)`
 *  behaviour exactly (including the circle point-count default). `size` is the full width×height
 *  (shape_size, [x, y]); the shape is centred on the origin. */
export function GetPointsMatchingShape(size: [number, number]|n, shape_type: ShapeTypeStr|n, circle_pointCount = 16): Vec2Simple[] {
	if (shape_type == null) {
		// A null/absent shape_type: fall back to a unit square so callers don't crash on an
		// incompletely-authored object. (The old UI path never called this without a shape_type,
		// but the accessor path can.)
		return RectPoints(1, 1);
	}
	const sx = size == null ? 1 : size[0];
	const sy = size == null ? 1 : size[1];
	const hsx = sx / 2, hsy = sy / 2;

	switch (shape_type) {
		case "ellipse": {
			if (hsx <= 0 || hsy <= 0) return [[0, 0]]; // zero/negative size → single centre point
			const points: Vec2Simple[] = [];
			for (let i = 0; i < circle_pointCount; i++) {
				const angle = (i / circle_pointCount) * Math.PI * 2;
				points.push([hsx * Math.cos(angle), hsy * Math.sin(angle)]);
			}
			return points;
		}
		case "rectangle":
			return RectPoints(sx, sy);
		default:
			throw new Error(`GetPointsMatchingShape: Unsupported shape_type "${String(shape_type)}"`);
	}
}

/** Axis-aligned rectangle footprint centred on the origin: CCW-wound so the interior face is on
 *  the left when traversing (matching the plan's interior-face convention). */
function RectPoints(width: number, height: number): Vec2Simple[] {
	const hx = width / 2, hy = height / 2;
	return [[-hx, -hy], [-hx, hy], [hx, hy], [hx, -hy]];
}
